/* Material / size radio cards — variant matching + sibling navigation.
 *
 * Ported and trimmed from the production store's hc-product-variant-picker-v2
 * custom element. Two changes from the source:
 *
 *   1. No custom-element registration — Horizon ships its own `variant-picker`
 *      and `swatches-variant-picker-component` elements; this stays a plain
 *      init-on-selector module like every other afroyla-pdp-*.js file, so
 *      there is no name collision and no Shadow DOM to fight.
 *   2. `_applyVariant` no longer replays at +100/500/1500ms. That hack existed
 *      because Broadcast's own picker (`_updateAddToCartButton`/
 *      `_updatePriceBlock`) raced this module and overwrote it later on the
 *      same tick. Here this module IS the only writer of the headline price
 *      and the sold-out state, so one synchronous write is enough; the
 *      MutationObserver convergers in afroyla-atc-price-pill.js and
 *      afroyla-coupon-price-sync.js still mop up anything a coupon claim
 *      changes afterward.
 *
 * Dispatches theme:variant:change on the section, which is the single event
 * every ported CRO module (coupon strip, price sync, set picker, preorder,
 * price pill) already listens for. */
(function () {
  'use strict';

  function scopeOf(node) {
    return node.closest('.shopify-section') || node.closest('[data-section-id]') || document;
  }

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* Writes the ATC verb into every [data-add-to-cart-text] in `scope`,
   * preserving the `.btn__price` child exactly the way afroyla-preorder.js's
   * own `relabel()` does (assets/afroyla-preorder.js:62-74) — same DOM
   * shape, so whichever module writes last never clobbers the price suffix
   * the other one appended. */
  function relabelText(scope, verb) {
    scope.querySelectorAll('[data-add-to-cart-text]').forEach(function (el) {
      var price = el.querySelector('.btn__price');
      if (price) {
        el.textContent = verb + ' ';
        el.appendChild(price);
      } else {
        el.textContent = verb;
      }
    });
  }

  function init(root) {
    if (root.__init) return;
    root.__init = true;

    var script = root.querySelector('[data-mc-variants]');
    var variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }

    var scope = scopeOf(root);
    var isNavigating = false;

    function selects() {
      return Array.prototype.slice
        .call(root.querySelectorAll('select.afroyla-mc__select'))
        .sort(function (a, b) {
          return Number(a.getAttribute('data-option-index')) - Number(b.getAttribute('data-option-index'));
        });
    }

    function findMatchingVariant() {
      var chosen = selects().map(function (s) {
        return s.value;
      });
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var idx = 0; idx < chosen.length; idx++) {
          if (v['option' + (idx + 1)] !== chosen[idx]) ok = false;
        }
        if (ok) return v;
      }
      return null;
    }

    function updateFormInput(variantId) {
      var input = scope.querySelector('form[data-product-form] input[name="id"]');
      if (input) input.value = variantId;
    }

    function updateCards() {
      root.querySelectorAll('.afroyla-mc__card').forEach(function (card) {
        var radio = card.querySelector('.afroyla-mc__cardradio');
        card.classList.toggle('is-selected', !!(radio && radio.checked));
      });
      // Swatch groups print the chosen value in their label ("LETTER: M").
      root.querySelectorAll('[data-mc-selected]').forEach(function (el) {
        var select = root.querySelector('select.afroyla-mc__select[data-option-index="' + el.getAttribute('data-mc-selected') + '"]');
        if (select) el.textContent = select.value;
      });
    }

    // Verb ownership on a variant switch (code review finding H2): sold-out
    // always wins outright and is owned here, since afroyla-preorder.js's
    // own `apply()` explicitly leaves the label alone once
    // `variant.available === false` (it assumes an old theme already wrote
    // "Sold out" server-side — this Horizon build does not). For the
    // available case, this module only writes the plain verb when there is
    // no pre-order pane in scope to hand it to; when panes exist,
    // afroyla-preorder.js's own `theme:variant:change` listener (which fires
    // synchronously right after this one, via dispatchVariantChange below)
    // recomputes Pre-order/Add to cart from the selected variant's pane and
    // must be the only writer, or the two would race and flicker.
    function updateAddToCartButton(variant) {
      var btn = scope.querySelector('[data-add-to-cart]');
      if (!btn) return;
      var unavailable = !variant || variant.available === false;
      btn.disabled = unavailable;
      btn.classList.toggle('is-sold-out', unavailable);

      if (unavailable) {
        relabelText(scope, 'Sold out');
      } else if (!scope.querySelector('.apo-pane')) {
        var verb = (window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to cart';
        relabelText(scope, verb);
      }
    }

    function updatePriceBlock(variant) {
      var priceEl = scope.querySelector('[data-product-price]');
      if (priceEl && typeof variant.price === 'number') {
        priceEl.textContent = formatMoney(variant.price);
      }
      var compareEl = scope.querySelector('[data-compare-price]');
      var hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareEl) {
        if (hasDiscount) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.classList.add('hidden');
        }
      }
    }

    function dispatchVariantChange(variant) {
      scope.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { variant: variant }, bubbles: true }));
    }

    function applyVariant(variant) {
      updateFormInput(variant.id);
      updateCards();
      updateAddToCartButton(variant);
      updatePriceBlock(variant);
      dispatchVariantChange(variant);
    }

    function onSelectChange(select) {
      var selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.getAttribute('data-sibling-url')) {
        navigateToSibling(selectedOption.getAttribute('data-sibling-url'));
        return;
      }
      var variant = findMatchingVariant();
      if (!variant) return;
      applyVariant(variant);
    }

    selects().forEach(function (select) {
      select.addEventListener('change', function () {
        onSelectChange(select);
      });
    });

    root.querySelectorAll('.afroyla-mc__cardradio').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        var siblingUrl = radio.getAttribute('data-sibling-url');
        if (siblingUrl) {
          navigateToSibling(siblingUrl);
          return;
        }
        var idx = radio.getAttribute('data-for-select');
        var select = root.querySelector('select.afroyla-mc__select[data-option-index="' + idx + '"]');
        if (!select || select.value === radio.value) return;
        select.value = radio.value;
        select.dispatchEvent(new Event('change'));
      });
    });

    /* Cross-product size navigation. Sizes are separate PRODUCTS (own
     * reviews, own preorder batches), so a size click must land on the
     * sibling's markup — but it should FEEL like Skullette's in-product
     * variant switch (owner ask 2026-08-22). Two speedups over the ported
     * full-page fetch:
     *
     *   1. Section Rendering API: `?section_id=<this section>` returns just
     *      the product section (works because siblings share the product
     *      template). Falls back to the full-page fetch if the sibling
     *      renders a different template.
     *   2. Idle-time prefetch of every sibling section on init, so the
     *      common click path swaps synchronously from cache — no dim, no
     *      spinner. */
    var sectionWrapperForId = root.closest('.shopify-section');
    var sectionId = null;
    if (sectionWrapperForId && sectionWrapperForId.id.indexOf('shopify-section-') === 0) {
      sectionId = sectionWrapperForId.id.slice('shopify-section-'.length);
    }
    var siblingCache = Object.create(null); // section-fetch URL -> innerHTML (null = in flight / failed)
    var siblingJson = Object.create(null); // pathname -> /products/<handle>.js payload

    /* The non-size choices on THIS page (e.g. Material) must survive a size
     * swap — Skullette does this via ?variant= and the owner benchmarked it
     * (2026-08-22). Sibling option POSITIONS differ from ours (same finding
     * as the group-order fix), so variants are matched by option NAME. */
    function nonSizeSelections() {
      var map = {};
      selects().forEach(function (s) {
        var name = (s.getAttribute('data-option-name') || '').toLowerCase();
        if (name.indexOf('size') === -1) map[name] = s.value;
      });
      return map;
    }

    function fetchSiblingJson(pathname) {
      if (siblingJson[pathname]) return Promise.resolve(siblingJson[pathname]);
      return fetch(pathname + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (json) {
          if (json) siblingJson[pathname] = json;
          return json;
        })
        .catch(function () { return null; });
    }

    /* Sibling products spell the same choice differently — verified live:
     * Medium's "Top-Grain Leather (Italian leather. Buttery-soft.)" vs
     * Small's "Top-Grain Leather". Compare with parentheticals stripped and
     * punctuation collapsed, exact-normalized first, prefix as fallback. */
    function normOptValue(v) {
      return String(v || '')
        .toLowerCase()
        .replace(/\([^)]*\)/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    function optValuesMatch(a, b) {
      var na = normOptValue(a);
      var nb = normOptValue(b);
      if (!na || !nb) return false;
      return na === nb || na.indexOf(nb) === 0 || nb.indexOf(na) === 0;
    }

    function matchSiblingVariant(json, requireAvailable) {
      var want = nonSizeSelections();
      var names = (json.options || []).map(function (o) {
        return ((o && o.name) || o || '').toLowerCase();
      });
      var found = null;
      (json.variants || []).forEach(function (v) {
        if (found) return;
        if (requireAvailable && !v.available) return;
        var ok = true;
        names.forEach(function (name, i) {
          if (name.indexOf('size') !== -1) return;
          if (want[name] !== undefined && !optValuesMatch(v.options[i], want[name])) ok = false;
        });
        if (ok) found = v;
      });
      return found;
    }

    function resolveSiblingVariantId(pathname) {
      return fetchSiblingJson(pathname).then(function (json) {
        if (!json) return null;
        var v = matchSiblingVariant(json, true) || matchSiblingVariant(json, false);
        return v ? v.id : null;
      });
    }

    function sectionFetchUrl(pathname, variantId) {
      if (!sectionId) return null;
      var url = pathname + '?section_id=' + encodeURIComponent(sectionId);
      if (variantId) url += '&variant=' + variantId;
      return url;
    }

    function fetchSiblingSection(fetchUrl) {
      if (!fetchUrl) return Promise.resolve(null);
      return fetch(fetchUrl)
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) {
          if (!html) return null;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('[data-afroyla-product-section]');
          if (!section) return null;
          var wrapper = section.closest('.shopify-section');
          return wrapper ? wrapper.innerHTML : doc.body.innerHTML;
        })
        .catch(function () { return null; });
    }

    /* Decode the sibling gallery's lead images ahead of the click — a swap
     * into undecoded images is the flash the owner sees; decoded ones paint
     * on the first frame (Skullette reads smooth because identical nodes
     * survive its morph; pre-decode + the view transition in swapInSibling
     * is our equivalent). */
    function warmSiblingImages(html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var imgs = doc.querySelectorAll('img');
        for (var i = 0; i < imgs.length && i < 4; i++) {
          var src = imgs[i].currentSrc || imgs[i].getAttribute('src');
          if (!src) continue;
          var im = new Image();
          if (imgs[i].getAttribute('srcset')) im.srcset = imgs[i].getAttribute('srcset');
          im.src = src;
          if (im.decode) im.decode().catch(function () {});
        }
      } catch (e) {
        /* warm-up only — never blocks the swap */
      }
    }

    function prefetchSiblings() {
      root.querySelectorAll('[data-sibling-url]').forEach(function (el) {
        var pathname;
        try {
          pathname = new URL(el.getAttribute('data-sibling-url'), window.location.origin).pathname;
        } catch (e) {
          return;
        }
        resolveSiblingVariantId(pathname).then(function (variantId) {
          var fetchUrl = sectionFetchUrl(pathname, variantId);
          if (!fetchUrl || fetchUrl in siblingCache) return;
          siblingCache[fetchUrl] = null;
          fetchSiblingSection(fetchUrl).then(function (html) {
            if (html) {
              siblingCache[fetchUrl] = html;
              warmSiblingImages(html);
            }
          });
        });
      });
    }
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prefetchSiblings, { timeout: 3000 });
    } else {
      setTimeout(prefetchSiblings, 800);
    }
    /* A material change re-targets which sibling variant a size click needs —
     * warm the cache for the new selection (old entries stay keyed apart). */
    scope.addEventListener('theme:variant:change', function () {
      if (window.requestIdleCallback) window.requestIdleCallback(prefetchSiblings, { timeout: 2000 });
      else setTimeout(prefetchSiblings, 300);
    });

    function swapInSibling(sectionWrapper, innerHTML, productUrl) {
      var apply = function () {
        sectionWrapper.innerHTML = innerHTML;

        sectionWrapper.querySelectorAll('script:not([src])').forEach(function (oldScript) {
          var type = (oldScript.getAttribute('type') || '').toLowerCase();
          if (type && type !== 'text/javascript' && type !== 'module') return;
          var newScript = document.createElement('script');
          for (var i = 0; i < oldScript.attributes.length; i++) {
            newScript.setAttribute(oldScript.attributes[i].name, oldScript.attributes[i].value);
          }
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        history.pushState({ afroylaSiblingNav: true, productUrl: productUrl }, '', productUrl);
      };

      /* The rebuild-everything swap is what reads as a blink (Horizon's own
       * variant changes morph instead, so nothing repaints). A view
       * transition lets the compositor cross-fade old pixels into new —
       * identical regions don't visibly change at all. Horizon's base.css
       * sets `:root { view-transition-name: none }` (its own typed page
       * transitions), so the section must carry its own name for the
       * capture to happen at all. Fallback: plain swap. */
      if (document.startViewTransition) {
        sectionWrapper.style.viewTransitionName = 'afl-size-swap';
        var vt = document.startViewTransition(apply);
        vt.finished.then(
          function () { sectionWrapper.style.viewTransitionName = ''; },
          function () { sectionWrapper.style.viewTransitionName = ''; }
        );
      } else {
        apply();
      }
    }

    function navigateToSibling(productUrl) {
      var url;
      try {
        url = new URL(productUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        productUrl = url.pathname;
      } catch (e) {
        return;
      }
      if (isNavigating) return;
      isNavigating = true;

      var sectionWrapper = root.closest('.shopify-section');
      if (!sectionWrapper) {
        window.location.href = productUrl;
        return;
      }

      resolveSiblingVariantId(productUrl).then(function (variantId) {
        // The address the shopper keeps: sibling page with the carried-over
        // choice pinned, so refresh/share land on the same variant.
        var destUrl = productUrl + (variantId ? '?variant=' + variantId : '');
        var fetchUrl = sectionFetchUrl(productUrl, variantId);

        var cached = fetchUrl && siblingCache[fetchUrl];
        if (cached) {
          swapInSibling(sectionWrapper, cached, destUrl);
          isNavigating = false;
          return;
        }

        sectionWrapper.style.transition = 'opacity 0.2s ease';
        sectionWrapper.style.opacity = '0.4';
        sectionWrapper.style.pointerEvents = 'none';

        fetchSiblingSection(fetchUrl)
          .then(function (html) {
            if (html) return html;
            // Sibling on a different template — fall back to the full page.
            return fetch(destUrl)
              .then(function (res) {
                if (!res.ok) throw new Error('HTTP ' + res.status);
                return res.text();
              })
              .then(function (pageHtml) {
                var doc = new DOMParser().parseFromString(pageHtml, 'text/html');
                var siblingSection = doc.querySelector('[data-afroyla-product-section]');
                var siblingWrapper = siblingSection ? siblingSection.closest('.shopify-section') : null;
                if (!siblingWrapper) throw new Error('product section not found in sibling page');
                return siblingWrapper.innerHTML;
              });
          })
          .then(function (innerHTML) {
            sectionWrapper.style.opacity = '1';
            sectionWrapper.style.pointerEvents = '';
            swapInSibling(sectionWrapper, innerHTML, destUrl);
          })
          .catch(function () {
            sectionWrapper.style.opacity = '1';
            sectionWrapper.style.pointerEvents = '';
            window.location.href = destUrl;
          })
          .then(function () {
            isNavigating = false;
          });
      });
    }

    if (!window.__afroylaSiblingPopstate) {
      window.__afroylaSiblingPopstate = true;
      window.addEventListener('popstate', function (event) {
        if (event.state && event.state.afroylaSiblingNav) window.location.reload();
      });
    }
  }

  function adoptAll(node) {
    (node || document).querySelectorAll('[data-material-cards]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { adoptAll(document); });
  } else {
    adoptAll(document);
  }

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-material-cards]')) init(node);
          else if (node.querySelector && node.querySelector('[data-material-cards]')) adoptAll(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
